/**
 * MetadataFormField Component
 *
 * Renders a single form field based on metadata
 * Pure metadata-driven with NO hardcoded field logic
 */

import React from 'react'
import { Form, Input, InputNumber, Switch, Select, Typography } from 'antd'
import type { MetadataFormFieldProps } from './types'
import { getFieldComponent } from './utils/fieldMapping'
import { buildValidationRules } from './utils/validation'
import { SemanticDatePicker } from '../SemanticDatePicker'
import { JsonValueDisplay } from '../MetadataTable/components/JsonValueDisplay'
import { useTimezone } from '@/contexts/TimezoneContext'
import { showInForm, FieldMetadata } from '@/services/metadataService'

const { Text } = Typography

/**
 * Wrapper component for JSON fields in forms.
 * Receives value from Form.Item and renders JsonValueDisplay.
 */
interface JsonFormFieldProps {
  value?: unknown
}

const JsonFormField: React.FC<JsonFormFieldProps> = ({ value }) => {
  // Handle null/undefined
  if (value === null || value === undefined) {
    return <Text type="secondary">-</Text>
  }

  // Use JsonValueDisplay for objects/arrays
  if (typeof value === 'object') {
    return <JsonValueDisplay value={value} maxHeight={300} collapsed={2} />
  }

  // Fallback for primitive values
  return <Text>{String(value)}</Text>
}

export const MetadataFormField: React.FC<MetadataFormFieldProps> = ({
  fieldName,
  metadata,
  disabled = false,
  minDate,
  maxDate,
  disabledDateMessage,
}) => {
  // Get organization timezone for date picker
  const { timezone } = useTimezone()

  // Skip fields that shouldn't appear in forms (based on category and visibility)
  if (!showInForm(metadata as FieldMetadata)) {
    return null
  }

  // Check if field should be disabled (schema readonly OR parent disabled)
  const isDisabled = metadata.readonly || disabled

  // Get component type and props based on py_type
  const { component, props, valuePropName } = getFieldComponent(metadata)

  // Build validation rules from metadata
  const rules = buildValidationRules(fieldName, metadata)

  // Render appropriate component
  const renderComponent = () => {
    switch (component) {
      case 'Input':
        return (
          <Input
            placeholder={metadata.description || `Enter ${metadata.title}`}
            disabled={isDisabled}
            {...props}
          />
        )

      case 'InputNumber':
        return (
          <InputNumber
            style={{ width: '100%' }}
            min={metadata.min_value ?? undefined}
            max={metadata.max_value ?? undefined}
            placeholder={metadata.description || `Enter ${metadata.title}`}
            disabled={isDisabled}
            {...props}
          />
        )

      case 'Switch':
        return <Switch disabled={isDisabled} {...props} />

      case 'Select':
        if (metadata.allowed_values) {
          return (
            <Select
              placeholder={metadata.description || `Select ${metadata.title}`}
              disabled={isDisabled}
              {...props}
            >
              {metadata.allowed_values.map((option) => {
                const optionValue = typeof option === 'string' ? option : option.value
                const optionLabel = typeof option === 'string' ? option : option.label
                return (
                  <Select.Option key={optionValue} value={optionValue}>
                    {optionLabel}
                  </Select.Option>
                )
              })}
            </Select>
          )
        }
        // Generic select (consumer must provide options via field overrides)
        return (
          <Select
            placeholder={metadata.description || `Select ${metadata.title}`}
            disabled={isDisabled}
            {...props}
          />
        )

      case 'DatePicker':
        // Schema-driven date picker with semantic NULL representation
        // Uses date_semantics from metadata to determine behavior
        // Note: value and onChange are injected by Form.Item
        return (
          <SemanticDatePicker
            dateSemantics={metadata.date_semantics}
            showTime={props?.showTime}
            placeholder={metadata.description || `Select ${metadata.title}`}
            disabled={isDisabled}
            timezone={timezone}
            minDate={minDate}
            maxDate={maxDate}
            disabledDateMessage={disabledDateMessage}
          />
        )

      case 'TextArea':
        return (
          <Input.TextArea
            rows={4}
            placeholder={metadata.description || `Enter ${metadata.title}`}
            disabled={isDisabled}
            {...props}
          />
        )

      case 'JsonViewer':
        // JSON fields use JsonValueDisplay for interactive viewing
        // Form.Item passes value via valuePropName, but we need a custom wrapper
        return <JsonFormField />

      default:
        return (
          <Input
            placeholder={metadata.description || `Enter ${metadata.title}`}
            disabled={isDisabled}
            {...props}
          />
        )
    }
  }

  return (
    <Form.Item
      name={fieldName}
      label={metadata.title || fieldName}
      rules={rules}
      valuePropName={valuePropName || 'value'}
      initialValue={metadata.default} // Set defaults immediately to avoid race conditions
      tooltip={metadata.description}
    >
      {renderComponent()}
    </Form.Item>
  )
}
